import { entryFilesService } from '@patient/services/entry/entryFilesService';
import { AnyObject } from '@store/types';
import sortBy from 'lodash/sortBy';
import map from 'lodash/map';
import flatMap from 'lodash/flatMap';
import uniqBy from 'lodash/uniqBy';
import compact from 'lodash/compact';
import autobind from 'autobind';

import { getUniqIds } from '@utils/getUniqIds';
import { isUuid } from '@utils/isUUID';

import {
    CreateEntryResponse,
    DiagnosisParams,
    EhrFieldsData,
    EntryAudioRecordCreateParams,
    EntryAudioRecordData,
    EntryCreateForm,
    EntryData,
    EntryDrugParams,
    EntryFieldData,
    EntryListItem,
    EntryStatus,
    EntryTranslationParams,
    EntryTypeData,
    EntryTypeFieldType,
    EntryTypeItemPresentationData,
    EntryTypeItemPresentationSubject,
    FieldValueItem,
    FileViewModel,
    FrontendDictionaryNames,
    ReferralData,
    RenderedServiceData,
    SoapTypeData,
    UpdateFieldForm,
} from 'chaika-frontend-types/frontend-cms';
import { EhrViewContext, EntryAudioRecordStatus, EntryTranslationStatus, Locale } from 'chaika-enums';

import { EntryApi } from '@api/EntryApi';
import { EntryTypeApi } from '@api/EntryTypeApi';
import { EhrApi } from '@api/EhrApi';

import { store } from '@store';
import { Entry, SoapEntryTypeNames } from '@patient/store/entry/Entry';
import { CommonRelation, commonService } from '@store/common/commonService';
import { EntryFields } from '@store/models/EntryFields/EntryFields';
import { EntryType } from '@template/store/entryType/EntryType';
import { entryTypeService } from '@template/services/entryType/entryTypeService';
import { serviceService } from '@services/services/serviceService';
import { planService } from '@plan/service/planService';
import { networkService } from '@organization/service/network/networkService';
import { dictionaryService } from '@template/services/dictionary/dictionaryService';
import { EntryTypeItemPresentationApi } from '@api/EntryTypeItemPresentationApi';
import { userService } from '@user/services/userService';
import { authUserService } from '@store/AuthUser/services/authUserService';
import { DateHelper } from '@utils/DateHelper';
import { assertIsDefined } from '@utils/assertIsDefined';

import { entryReservationService } from './entryReservationService';
import { updateEntryFieldService } from './updateEntryFieldService';
import { referralService } from './referralService';
import { entryMedicalCaseService } from './entryMedicalCaseService';
import { sortByCreatedAtDesc } from './sortByCreatedAtDesc';
import { EntryTypeField } from '@template/store/entryType/EntryTypeField';
import { TableField } from '@store/models/EntryFields';
import { ehrsServicePlansService } from '../ehr/ehrsServicePlansService';
import { entryLazyUpdater } from '@patient/services/entry/EntryLazyUpdater';
import { EntryTranslationApi } from '@api/EntryTranslationApi';
import { translate } from '@chaika/i18n/helper';
import { staticEntryTypeService } from '@patient/services/entry/staticEntryTypeService';
import type { EntryTypeItemPresentationUpdateParams } from 'chaika-types';
import { EntryAudioRecordsApi } from '@api/EntryAudioRecordsApi';

export interface CreateEntryParams extends Omit<EntryCreateForm, 'organizationId'> {
    typeId?: string;
}

export enum EntryRelation {
    EntryType = 'entryType',
    RedesignEntryExpandedData = 'redesignEntryExpandedData',
    Versions = 'versions',
    Translations = 'translations',
    EntryTypeItemPresentation = 'entryTypeItemPresentation',
    EntryAudioRecord = 'entryAudioRecord',
}

export enum WellKnownAllowToAssistantEntryTypeIds {
    AttachedDocument = 'daa7b1b3-331c-4492-8697-67f63b1e4484',
}

class EntryService {
    private relationLoader = {
        [EntryRelation.EntryType]: this.loadEntryType,
        [EntryRelation.RedesignEntryExpandedData]: this.loadRedesignedEntryExpandedData,
        [EntryRelation.Versions]: this.loadVersions,
        [EntryRelation.Translations]: this.loadTranslations,
        [EntryRelation.EntryTypeItemPresentation]: this.loadEntryTypeItemPresentation,
        [EntryRelation.EntryAudioRecord]: this.loadEntryAudioRecord,
    };

    public isAllowToSignByAssistant(typeId: string): boolean {
        const allowToSignByAssistantEntryTypes = store.dictionary
            .getDictionary(FrontendDictionaryNames.AllowToAssistantEntryType)
            .values.map(value => value.value);

        return allowToSignByAssistantEntryTypes.includes(typeId);
    }

    public async createEntry(
        params: CreateEntryParams,
        walkIn: boolean,
        medicalCaseId?: string,
    ): Promise<CreateEntryResponse | void> {
        const { authUser } = store;
        const { organizationId } = authUser;

        if (organizationId) {
            return EntryApi.create({
                ...params,
                walkIn,
                organizationId,
                medicalCaseId,
                legalEntityId: authUserService.getSelectedOrganizationLegalEntityId(),
            });
        }
    }

    public async loadEntry(id: string, relations?: EntryRelation[], viewContext?: EhrViewContext): Promise<void> {
        const [{ entry, ehrFields: originEhrFields, files }] = await Promise.all([
            EntryApi.getEntry(id, viewContext),
            commonService.load([CommonRelation.FieldsParams]),
        ]);
        const ehrFields = await this.makeEhrFields(originEhrFields, entry);
        const userIds = compact([entry.doctorId, entry.authorId, ...(entry?.coAuthorIds || [])]);

        Object.assign(store.entry, new Entry(entry));
        this.setEhrFieldData(ehrFields);
        this.setFiles(files);

        await Promise.all([
            entryMedicalCaseService.load(),
            entryReservationService.load(),
            entryFilesService.loadEDS(),
            userService.loadIfNotExist(userIds),
            this.loadFieldRelationData(entry.fields, ehrFields, entry.creationTime, entry.authorId, entry.coAuthorIds),
        ]);

        if (relations) {
            await this.loadRelations(relations);
        }
    }
    public async loadRelations(relations: EntryRelation[]): Promise<void> {
        await Promise.all(relations.map(relation => this.relationLoader[relation]()));
    }

    public async checkoutVersion(versionId: string): Promise<void> {
        const entry = store.entry;

        const entryVersion = entry.entryVersions.getExisting(versionId);

        await EntryApi.checkoutVersions(entry.id, entryVersion.updateTime, entry.ehrId);

        const {
            entry: { fields },
            ehrFields,
        } = await EntryApi.getEntry(entry.id);
        entry.fields = new EntryFields(fields);
        this.setEhrFieldData(ehrFields);

        entry.isViewMode = false;
    }

    public async setDraftStatus(): Promise<void> {
        const newStatus = EntryStatus.Draft;
        await EntryApi.updateStatus(store.entry.id, newStatus);
        store.entry.status = newStatus;
        await Promise.all([
            entryFilesService.removeSingEntrtFile(store.entry.id),
            referralService.reloadReferrals(),
            entryMedicalCaseService.load(),
        ]);

        store.entry.setEDS();

        const { files } = await EntryApi.getEntry(store.entry.id);
        this.setFiles(files);
    }

    public async updateDoctor(doctorId: string): Promise<void> {
        await EntryApi.updateDoctor(store.entry.id, doctorId);
        store.entry.doctorId = doctorId;
    }

    public async toggleInDoor(): Promise<void> {
        const inDoor = !store.entry.inDoor;
        await EntryApi.updateData(store.entry.id, { inDoor });
        store.entry.inDoor = inDoor;
    }

    public async updateDevice(deviceId: string): Promise<void> {
        await EntryApi.updateData(store.entry.id, { deviceId });
        store.entry.deviceId = deviceId;
    }

    public async updateOffice(officeId: string | undefined): Promise<void> {
        await EntryApi.updateData(store.entry.id, { officeId: officeId ?? null });
        store.entry.officeId = officeId;
    }

    public async setReadyForDescribingStatus(): Promise<void> {
        const newStatus = EntryStatus.ReadyForDescribing;
        await EntryApi.updateStatus(store.entry.id, newStatus);
        store.entry.status = newStatus;
    }

    public async setSignedStatus(): Promise<void> {
        const entry = store.entry;
        const newStatus = EntryStatus.Signed;
        await updateEntryFieldService.updateFieldImmediately();
        await EntryApi.updateStatus(entry.id, newStatus);
        let signedAt = new Date().toISOString();
        try {
            const { entry: signedEntry } = await EntryApi.getEntry(entry.id);
            entry.fields = new EntryFields(signedEntry.fields);
            entry.updateTime = signedEntry.updateTime;
            signedAt = signedEntry.signedAt ?? signedAt;
        } finally {
            entry.status = newStatus;
            entry.signedAt = signedAt;
        }
        if (store.entryPage.isRedesign) {
            await entryFilesService.updateSingEntryFile(entry.id);
        }
    }

    public async addCoAuthor(): Promise<void> {
        await EntryApi.addCoAuthor(store.entry.id);
    }

    public async changeCoAuthors(coAuthorIds: string[]): Promise<void> {
        await EntryApi.changeCoAuthors(store.entry.id, coAuthorIds);
        store.entry.coAuthorIds = coAuthorIds;
    }

    public async updateType(typeId: string, typeVersion: number, isSoap = false): Promise<void> {
        const entry = store.entry;

        await EntryApi.updateType(entry.id, typeId);

        entry.typeId = typeId;
        entry.typeVersion = typeVersion;
        if (isSoap) {
            await this.loadRedesignedEntryExpandedData();
        } else {
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            await entryService.loadEntryType();
        }
        await this.saveInherentData();
    }

    public async editVisitTime(visitTime: string): Promise<void> {
        await EntryApi.editVisitTime(store.entry.id, {
            visitTime,
        });
    }

    public async updateNote(note: string): Promise<void> {
        const entry = store.entry;

        await EntryApi.updateNote(entry.id, note);
        entry.note = note;
    }

    public async updateInsuranceApproval(insuranceApproval: string): Promise<void> {
        const { entry, authUser } = store;

        await EntryApi.updateInsuranceApproval(entry.id, insuranceApproval);
        entry.insuranceApproval = insuranceApproval;
        entry.insuranceApprovalVersions = [
            ...entry.insuranceApprovalVersions,
            {
                insuranceApproval,
                authorId: authUser.id,
                // Время отличается на сервере
                versionDate: DateHelper.now().toISOString(),
            },
        ];
    }

    public async loadInsuranceApprovalHistory(): Promise<void> {
        const { entry } = store;

        const insuranceApprovalVersions = !!entry.id ? await EntryApi.getInsuranceApprovalHistory(entry.id) : [];
        await userService.loadIfNotExist(insuranceApprovalVersions.map(({ authorId }) => authorId));
        entry.insuranceApprovalVersions = insuranceApprovalVersions;
    }

    public async changeUrgency(isUrgent?: boolean): Promise<void> {
        const entry = store.entry;

        const newUrgency = entry.changeUrgency(isUrgent);
        await EntryApi.updateUrgency(entry.id, newUrgency);
    }

    public async getTodayEntriesForPatient(patientId: string): Promise<EntryListItem[]> {
        const { authUser, app } = store;
        app.isOnUpdate = true;
        const workDayStartTime = DateHelper.addHours(DateHelper.getStartOfDay(), 7).toISOString();
        const entryList = await EntryApi.getList({
            ehrIds: patientId,
            authorId: authUser.id,
            creationTimeFrom: workDayStartTime,
        });
        app.isOnUpdate = false;

        return entryList;
    }

    public async loadEntryAudioRecord(): Promise<void> {
        const { entry } = store;

        try {
            const entryAudioRecordData: EntryAudioRecordData[] = await EntryAudioRecordsApi.findRecords({
                entryId: entry.id,
            });

            const sortedByCreatedAtDesc = sortByCreatedAtDesc(entryAudioRecordData);

            const terminalStatuses: string[] = [EntryAudioRecordStatus.Finished, EntryAudioRecordStatus.Error];
            const activeRecord = sortedByCreatedAtDesc.find(record => !terminalStatuses.includes(record.status));

            entry.audioRecord = activeRecord ?? sortedByCreatedAtDesc[0];
        } finally {
            entry.isAudioRecordLoaded = true;
        }
    }

    public async changeHideEntryTypeItemOnPrint({
        itemId,
        itemType,
        hideFromPatient,
    }: Pick<EntryTypeItemPresentationData, 'itemId' | 'itemType' | 'hideFromPatient'>): Promise<void> {
        await this.updateEntryTypeItemPresentation(itemId, { hideFromPatient, itemType });
    }

    public async changeOpenEntryTypeItemByDefault({
        itemId,
        itemType,
        byDefaultIsOpen,
    }: Pick<EntryTypeItemPresentationData, 'itemId' | 'itemType' | 'byDefaultIsOpen'>): Promise<void> {
        await this.updateEntryTypeItemPresentation(itemId, { byDefaultIsOpen, itemType });
    }

    public async createAudioRecord(creationParams: EntryAudioRecordCreateParams): Promise<void> {
        const { entry } = store;

        await EntryAudioRecordsApi.createRecord(entry.id, creationParams);

        const entryAudioRecordData: EntryAudioRecordData[] = await EntryAudioRecordsApi.findRecords({
            entryId: entry.id,
        });

        entry.audioRecord = sortByCreatedAtDesc(entryAudioRecordData)[0];
    }

    public async deleteAudioRecord(): Promise<void> {
        const { entry } = store;

        if (entry.audioRecord) {
            await EntryAudioRecordsApi.deleteRecord(entry.id, entry.audioRecord.id);

            entry.audioRecord = undefined;
        }
    }

    public updateUpdateTime(time: string): void {
        const entry = store.entry;

        entry.updateTime = time;
    }

    public async updateEntryTranslation(locale: Locale, translation: EntryTranslationParams): Promise<void> {
        const { app, entry } = store;

        try {
            const translationByLocale = entry.translations.getExisting(locale);

            translationByLocale.updateField(translation.field);
            await EntryTranslationApi.updateEntryTranslationField(entry.id, locale, translation);
        } catch (e: any) {
            app.showPopup({
                title: translate('failSaveTranslation'),
                description: translate('errorOnSaveTranslation'),
            });
            console.error(e);
        }
    }

    public async changeEntryTranslationStatus(locale: Locale, status: EntryTranslationStatus): Promise<void> {
        const { entry } = store;
        const translationByLocale = entry.translations.getExisting(locale);

        await EntryTranslationApi.updateEntryTranslationStatus(entry.id, locale, { status });
        translationByLocale.updateStatus(status);
    }

    public async makeTranslatedEntry(locale?: Locale, fromSoap?: boolean): Promise<void> {
        store.entry.setSelectedLang(locale);

        if (fromSoap) {
            await this.loadRedesignedEntryExpandedData();
        } else {
            await this.loadEntryType();
        }
    }

    private async saveInherentData(): Promise<void> {
        const entry = store.entry;
        const inherentFields = entry.type.fields.filter(item => item.data && item.data.inherent);
        const id = entry.id;
        let value: any;
        const fieldsToSave: { name: string; fieldForm: UpdateFieldForm }[] = [];

        for (const { name } of inherentFields) {
            value = entry.fields.getValue(name) || entry.ehrFields.getValue(name) || entry.computedFields.get(name);
            if (value) {
                entry.setFieldValue(name, value);
                fieldsToSave.push({
                    name,
                    fieldForm: { field: { value } },
                });
            }
        }

        if (fieldsToSave.length > 0) {
            const updateData: AnyObject = {};
            fieldsToSave.forEach(({ name, fieldForm }) => {
                updateData[name] = fieldForm;
            });
            await entryLazyUpdater.updateImmediately({
                id,
                updateData,
                ehrId: entry.ehrId,
            });
        }
    }

    private async loadEntryTypeItemPresentation(): Promise<void> {
        const { entry } = store;

        const entryTypeItemPresentationList = await EntryTypeItemPresentationApi.getList(entry.id);
        entry.entryTypeItemPresentationList.set(entryTypeItemPresentationList);
    }

    private async loadFieldRelationData(
        fields: EntryFieldData[],
        ehrFields: EhrFieldsData,
        creationTime: string,
        authorId: string,
        coAuthorIds: string[] = [],
    ): Promise<void> {
        const referrals = this.getFieldData<ReferralData>(fields, ehrFields, EntryTypeFieldType.Referrals);
        const renderedServices = this.getFieldData<RenderedServiceData>(
            fields,
            ehrFields,
            EntryTypeFieldType.RenderedService,
        );
        const diagnosis = this.getFieldData<DiagnosisParams>(fields, ehrFields, EntryTypeFieldType.MedicalCases);
        const drugs = this.getFieldData<EntryDrugParams>(fields, ehrFields, EntryTypeFieldType.Drug);
        const nestedFieldEntries = fields.find(field => field.name === EntryTypeFieldType.NestedEntryType);
        const nestedEntries: { typeId: string; version: number }[] = nestedFieldEntries
            ? JSON.parse(nestedFieldEntries.value as string)
            : [];

        const servicesIds = getUniqIds([...map(referrals, 'serviceId'), ...map(renderedServices, 'serviceId')]);

        const dictionaryItemIds = getUniqIds([
            ...map(diagnosis, 'icdId'),
            ...map(drugs, 'tradeName'),
            ...map(drugs, 'activeSubstance'),
            ...map(drugs, 'tradeNameIds'),
        ]).filter(isUuid);

        const plans = flatMap(
            [
                ...renderedServices,
                ...referrals.map(item => item.service ?? { plans: [] }),
                ...referrals.flatMap(item => item.compositeService?.compositeServices ?? []),
            ],
            'plans',
        );
        const userIds = [
            authorId,
            ...coAuthorIds,
            ...getUniqIds(map(referrals, 'userId')),
            ...map(referrals, 'authorId'),
        ];
        renderedServices.forEach(({ executorId, coExecutorIds = [] }) => {
            if (executorId) {
                userIds.push(executorId);
            }

            if (!!coExecutorIds && coExecutorIds.length > 0) {
                userIds.push(coExecutorIds[0]);
            }
        });
        const planIds = getUniqIds(map(plans, 'id'));
        const networkIds = getUniqIds(map(referrals, 'networkId'));
        const ehrId = store.ehr.mainEhrId ?? store.ehr.id ?? store.entry.ehrId;

        // @ts-ignore
        await Promise.all([
            ...nestedEntries.map(({ typeId, version }) => entryTypeService.loadEntryTypeTemplate(typeId, version)),
            planIds.length > 0 && planService.loadByIds(planIds, creationTime),
            servicesIds.length > 0 &&
                ehrsServicePlansService.loadServicePlans({
                    ehrId,
                    serviceIds: servicesIds,
                    actualOn: creationTime,
                }),
            servicesIds.length > 0 && serviceService.loadByIds(servicesIds),
            networkIds.length > 0 && networkService.loadNetworksPriceList(),
            userIds.length > 0 && userService.loadIfNotExist(userIds),
            dictionaryItemIds.length > 0 && dictionaryService.loadDictionaryItemsByIds(dictionaryItemIds),
        ]);
    }

    private async updateEntryTypeItemPresentation(
        itemId: string,
        entryTypeItemPresentation: EntryTypeItemPresentationUpdateParams &
            Required<
                Omit<EntryTypeItemPresentationUpdateParams, 'hideFromPatient' | 'byDefaultIsOpen' | 'subjectType'>
            >,
    ): Promise<void> {
        const { entry, authUser } = store;
        const { hideFromPatient, byDefaultIsOpen, itemType } = entryTypeItemPresentation;

        await EntryTypeItemPresentationApi.update(authUser.id, itemId, {
            entryId: entry.id,
            entryTypeItemPresentation: { hideFromPatient, byDefaultIsOpen, itemType },
        });

        if (entry.entryTypeItemPresentationList.getByItemId(itemId)) {
            entry.entryTypeItemPresentationList.update(itemId, { hideFromPatient, byDefaultIsOpen, itemType });
        } else {
            entry.entryTypeItemPresentationList.add([
                {
                    itemId,
                    itemType,
                    subjectId: authUser.id,
                    subjectType: EntryTypeItemPresentationSubject.User,
                    hideFromPatient: hideFromPatient ?? false,
                    byDefaultIsOpen: byDefaultIsOpen ?? false,
                },
                {
                    itemId,
                    itemType,
                    subjectId: entry.id,
                    subjectType: EntryTypeItemPresentationSubject.Entry,
                    hideFromPatient: hideFromPatient ?? false,
                    byDefaultIsOpen: byDefaultIsOpen ?? false,
                },
            ]);
        }
    }

    private getFieldData<T>(fields: EntryFieldData[], ehrFields: EhrFieldsData, systemType: EntryTypeFieldType): T[] {
        const entryData = fields.find(field => field.name === systemType);
        const ehrData = ehrFields[systemType] ?? [];

        const entryDataParams: (T | FieldValueItem<T>)[] = (entryData?.value || []) as T[] | FieldValueItem<T>[];
        const entryDataValue = entryDataParams.every(item => (item as FieldValueItem<T>).value instanceof Object)
            ? (entryDataParams as FieldValueItem<T>[]).map(item => item.value)
            : (entryDataParams as T[]);

        return [...entryDataValue, ...ehrData];
    }

    @autobind
    private async loadEntryType(): Promise<void> {
        const entry = store.entry;
        const authUser = store.authUser;

        const [{ sections, ...entryType }] = await Promise.all([
            EntryTypeApi.getVersion(entry.typeId, entry.typeVersion, entry.selectedLang ?? authUser.selectedLanguage),
            this.loadSoapEntryType(),
        ]);
        const entryTypeModel = new EntryType({
            ...entryType,
            sections: staticEntryTypeService.addFilledSoapSections(sections),
        });

        await this.loadTableDictionaries(entryTypeModel.tableFields);
        entry.entryType = entryTypeModel;
    }

    private createSoapEntryType(name: SoapEntryTypeNames, { sections, ...entryType }: EntryTypeData): void {
        const entry = store.entry;

        const model = new EntryType({
            ...entryType,
            sections: staticEntryTypeService.filterSoapSections(sections),
        });

        entry.soapEntryType.set(name, model);
    }

    @autobind
    private async loadRedesignedEntryExpandedData(): Promise<void> {
        const entry = store.entry;
        const authUser = store.authUser;

        const [entryType] = await Promise.all([
            EntryTypeApi.getVersion(entry.typeId, entry.typeVersion, entry.selectedLang ?? authUser.selectedLanguage),
            this.loadSoapEntryType(),
        ]);

        const filteredSections = staticEntryTypeService.filterSections(entryType.sections);
        const entryTypeModel = new EntryType({ ...entryType, sections: filteredSections });

        await this.loadTableDictionaries(entryTypeModel.tableFields);
        entry.entryType = entryTypeModel;
    }

    @autobind
    private async loadSoapEntryType(): Promise<void> {
        const entry = store.entry;
        const authUser = store.authUser;
        const entryTypesIds = [
            {
                typeId: entry.soapType?.subjectiveTypeId,
                typeVersion: entry.soapType?.subjectiveVersion,
            },
            {
                typeId: entry.soapType?.assessmentTypeId,
                typeVersion: entry.soapType?.assessmentVersion,
            },
            {
                typeId: entry.soapType?.planTypeId,
                typeVersion: entry.soapType?.planVersion,
            },
            {
                typeId: entry.soapType?.paymentTypeId,
                typeVersion: entry.soapType?.paymentVersion,
            },
        ];

        const [subjectiveEntryType, assessmentEntryType, planEntryType, paymentEntryType] = await Promise.all(
            entryTypesIds.map(ids => {
                assertIsDefined(ids.typeId, 'SOAP entry has all its entry types');
                assertIsDefined(ids.typeVersion, 'SOAP entry has all its entry type versions');
                return EntryTypeApi.getVersion(
                    ids.typeId,
                    ids.typeVersion,
                    entry.selectedLang ?? authUser.selectedLanguage,
                );
            }),
        );

        this.createSoapEntryType(SoapEntryTypeNames.SubjectiveEntryType, subjectiveEntryType);
        this.createSoapEntryType(SoapEntryTypeNames.AssessmentEntryType, assessmentEntryType);
        this.createSoapEntryType(SoapEntryTypeNames.PlanEntryType, planEntryType);
        this.createSoapEntryType(SoapEntryTypeNames.PaymentEntryType, paymentEntryType);
    }

    @autobind
    private async loadTableDictionaries(tableFields: EntryTypeField[]): Promise<void> {
        const dictionaryNames = compact(
            flatMap(tableFields, ({ data: { table } }) =>
                !!table ? TableField.getDictionaryNames(table.cells) : undefined,
            ),
        );

        await dictionaryService.load(dictionaryNames);
    }

    private async loadVersions(): Promise<void> {
        const entry = store.entry;

        const entryVersions = await EntryApi.getEntryVersion(entry.id);
        const entryVersionWithSoapType = entryVersions.map(it => ({
            ...it,
            soapType: it.soapType ?? ({} as SoapTypeData),
        }));

        entry.entryVersions.set(sortBy(entryVersionWithSoapType, 'updateTime').reverse());
    }

    private async loadTranslations(): Promise<void> {
        const entry = store.entry;

        const translations = await EntryTranslationApi.getEntryTranslations(entry.id);
        entry.translations.set(translations);
    }

    private async makeEhrFields(
        { medicalCases: entryMedicalCases = [], ...ehrFields }: EhrFieldsData,
        entry: EntryData,
    ): Promise<EhrFieldsData> {
        const currentEhrFields = await EhrApi.getEhrFieldDate(entry.ehrId);
        const referralMedicalCases = currentEhrFields.medicalCases || []; // .filter(medicalCase => referralMedicalCasesIdSet.has(medicalCase.id));

        const medicalCases = uniqBy([...entryMedicalCases, ...referralMedicalCases], 'id');

        return { medicalCases, ...ehrFields };
    }

    private setEhrFieldData(ehrFieldData: EhrFieldsData): void {
        const fieldNames = Object.keys(ehrFieldData);
        const ehrFields = fieldNames.map(name => ({
            name,
            value: ehrFieldData[name],
        }));

        store.entry.ehrFields = new EntryFields(ehrFields);
    }

    private setFiles(files: FileViewModel[]): void {
        store.entry.files = [];
        files.forEach(file => store.entry.addFile(file));
        store.entryPage.rightMenu.hasFiles = !!files.length;
    }
}

export const entryService = new EntryService();
